import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { UseFormSetValue } from 'react-hook-form'
import { Button, Fade, TextInputFieldProps } from 'utoniq-ui'

import { ItemType, UserPaymentType } from '@/clients/utoniq-core/schema'
import { CreateItemProps } from '@/components/pages/ConsoleItemCreate/template/ItemCreateForm'
import { config } from '@/config'
import { tLabel, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

import { TextInputCell } from './Commons'

type PriceInputCellProps = {
  mode: 'create' | 'edit'
  resourceType: ItemType
  setValue: UseFormSetValue<CreateItemProps>
  hasPrice: boolean
  inputProps: Omit<TextInputFieldProps, 't'>
  defaultPrice?: number
  paymentType: UserPaymentType | null
  disableRadio?: boolean
}
export const PriceInputCell: Cell<PriceInputCellProps> = ({
  mode,
  resourceType,
  hasPrice,
  setValue,
  inputProps,
  defaultPrice = 0,
  paymentType,
  disableRadio = false,
}) => {
  {
    /**
     * ポイントにも対応するアプリかつ現金払いに対応するアイテムかを判定
     */
  }
  const canPaymentOptionSelect = (
    hasPrice: boolean,
    paymentType: UserPaymentType | null,
    canPrepaidUse: boolean | null,
    resourceType: ItemType
  ) => {
    if (!hasPrice || !paymentType || !canPrepaidUse) return false
    return true // Enable cash payment option for all resource types
  }
  const { t } = useTranslation('consoleItemCreate')
  const options = [
    { value: true, label: t('availableForSale') },
    { value: false, label: t('notForSale') },
  ]
  return (
    <CellFrame sx={{ pb: 7 }}>
      {(mode === 'create' || hasPrice) && (
        <Typography variant="h5">{t('priceLabel')}</Typography>
      )}
      {mode === 'create' ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
            {t('priceDesc')}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ py: 2 }}>
            {options.map((option, index) => (
              <Button
                key={index}
                variant={hasPrice === option.value ? 'contained' : 'outlined'}
                onClick={() => {
                  setValue('hasPrice', option.value)
                  if (option.value) {
                    setValue('price', defaultPrice, {
                      shouldValidate: true,
                      shouldDirty: false,
                    })
                  } else {
                    setValue('price', 0, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                }}
              >
                {option.label}
              </Button>
            ))}
          </Stack>
        </>
      ) : (
        hasPrice && (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
            {t('priceForSaleEditDesc')}
          </Typography>
        )
      )}
      {canPaymentOptionSelect(
        hasPrice,
        paymentType,
        !!config.modules.prepaidPoint,
        resourceType
      ) && (
        <Box sx={{ mt: 2 }}>
          <RadioGroup
            onChange={(_, value) => {
              setValue('paymentType', value as UserPaymentType, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }}
            sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}
            value={paymentType}
          >
            <FormControlLabel
              value={UserPaymentType.PrepaidPoint}
              control={<Radio />}
              label={tLabel('consoleItemCreate.paymentType.prepaidPoint')}
              disabled={disableRadio}
            />
            <FormControlLabel
              value={UserPaymentType.Cash}
              control={<Radio />}
              label={tLabel('consoleItemCreate.paymentType.cash')}
              disabled={disableRadio}
            />
          </RadioGroup>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 2 }}>
            {t('paymentTypeDesc')}
          </Typography>
        </Box>
      )}
      {/* 金額 */}
      {hasPrice && (
        <Fade>
          <Stack direction="row" alignItems="center" spacing={2}>
            <TextInputCell textFieldProps={{ sx: { mb: 0 } }} {...inputProps} />
            <Typography variant="subtitle1" sx={{ pb: 4 }}>
              {paymentType === UserPaymentType.PrepaidPoint
                ? t('point')
                : t('円')}
            </Typography>
          </Stack>
        </Fade>
      )}
    </CellFrame>
  )
}
